import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  getAdminV1StoresStoreIdGiftCards,
  getAdminV1StoresStoreIdGiftCardsId,
  getAdminV1StoresStoreIdGiftCardsIdTransactions,
  patchAdminV1StoresStoreIdGiftCardsId,
  postAdminV1StoresStoreIdGiftCards,
  postAdminV1StoresStoreIdGiftCardsIdAdjust,
  postAdminV1StoresStoreIdGiftCardsIdSend,
  type GetAdminV1StoresStoreIdGiftCards200,
  type GiftCard,
  type GiftCardIssued,
  type GiftCardSent,
  type GiftCardTransaction,
  type PatchAdminV1StoresStoreIdGiftCardsIdBody,
  type PostAdminV1StoresStoreIdGiftCardsBody,
  type PostAdminV1StoresStoreIdGiftCardsIdAdjustBody,
  type PostAdminV1StoresStoreIdGiftCardsIdSendBody,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params: `sort`, the flat filter params (`status=active,expired`, `balance_gte=5000`),
 * `last4` for the search box, `customerId`, `cursor`, `limit`.
 */
export type GiftCardListParams = ListParams;

/**
 * Gift cards of one store. The code exists in the issue response only; lists show last4. Issuing
 * carries an `Idempotency-Key` so a retry after a timeout replays the first card instead of
 * minting a second one.
 */
export const giftCardsApi = {
  list: (
    storeId: string,
    params: GiftCardListParams,
  ): Promise<GetAdminV1StoresStoreIdGiftCards200> =>
    getAdminV1StoresStoreIdGiftCards({ storeId }, params),
  get: (storeId: string, id: string): Promise<GiftCard> =>
    getAdminV1StoresStoreIdGiftCardsId({ storeId, id }),
  issue: (
    storeId: string,
    body: PostAdminV1StoresStoreIdGiftCardsBody,
    idempotencyKey: string,
  ): Promise<GiftCardIssued> =>
    postAdminV1StoresStoreIdGiftCards({ storeId }, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdGiftCardsIdBody,
  ): Promise<GiftCard> => patchAdminV1StoresStoreIdGiftCardsId({ storeId, id }, body),
  adjust: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdGiftCardsIdAdjustBody,
  ): Promise<GiftCard> => postAdminV1StoresStoreIdGiftCardsIdAdjust({ storeId, id }, body),
  /** 202: the email is queued through the outbox; the answer names the address it goes to. */
  send: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdGiftCardsIdSendBody,
  ): Promise<GiftCardSent> => postAdminV1StoresStoreIdGiftCardsIdSend({ storeId, id }, body),
  transactions: (storeId: string, id: string): Promise<GiftCardTransaction[]> =>
    getAdminV1StoresStoreIdGiftCardsIdTransactions({ storeId, id }).then((r) => r.items),
};
